import { Suspense } from 'react';
import * as RD from '@radix-ui/react-dialog';
import { Navigate, Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { failureOf, setTechnicalReader } from '@/api/errors';
import { useMe } from '@/api/hooks';
import { RefusalPage } from '@/ui/RefusalPage';
import { ToastHost } from '@/ui/Toast';
import { ChatRail } from '@/chat/ChatRail';
import { MeContext } from './MeContext';
import { NavRail } from './NavRail';
import { FrameProvider, useFrame } from './frame';
import { TopBar } from './TopBar';
import { TabBar } from './TabBar';
import { CommandPalette } from './CommandPalette';
import { CalendarOverlay } from '@/today/CalendarOverlay';
import { startOfDay } from '@/today/grid';
import type { Me } from '@/api/session';
import { Booting } from './SignIn';
import { SavedTheme } from './useTheme';
import { VendorBanner } from '@/oversight/Plans';
import { PagePermissions } from './PagePermissions';
import { ScopeProvider } from './scope';
import { CompanyRouteBoundary } from './company-navigation';
import { RouteGuard } from './RouteGuard';

/**
 * The frame every signed-in page sits in: the rail, the page, the side chat.
 *
 * `/auth/me` decides what renders. A 401 means sign in — for every reason at
 * once, and the interface does not try to tell them apart. Anything else that
 * is not a person is shown as the refusal it is.
 */
export function Shell() {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <Booting />;
  if (me.isError) {
    const failure = failureOf(me.error);
    if (failure.kind === 'unauthenticated') {
      const returnTo = `${location.pathname}${location.search}`;
      return <Navigate to={`/signin?returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }
    return (
      <main className="min-h-dvh bg-page text-ink">
        <RefusalPage failure={failure} />
      </main>
    );
  }

  // A superadmin under the switch lands where it is released. Nobody else
  // gets this far while it is engaged: their `/auth/me` is a refusal.
  if (me.data.killSwitchEngaged && location.pathname !== '/safety') {
    return <Navigate to="/safety" replace />;
  }

  // Who reads the helpers that have no component to ask (a toast's words):
  // technical detail is the superadmin's alone.
  setTechnicalReader(me.data.isSuperadmin);

  return (
    <MeContext.Provider value={me.data}>
      <SavedTheme />
      <ScrollRestoration />
      <ToastHost>
        <FrameProvider me={me.data}>
          <ScopeProvider me={me.data}>
            <Frame me={me.data} />
          </ScopeProvider>
        </FrameProvider>
      </ToastHost>
    </MeContext.Provider>
  );
}

/**
 * The rail, the header over the page, the page, and the side chat — the
 * prototype's four regions (P:30-1300). The calendar opens over whichever
 * page is showing, from the header, as the prototype's does.
 */
function Frame({ me }: { me: Me }) {
  const frame = useFrame();
  const navigate = useNavigate();
  const fullChat = useLocation().pathname === '/chat';
  const today = startOfDay(new Date());
  return (
    <div className="flex min-h-dvh bg-page text-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-ink focus:px-5 focus:py-3.5 focus:text-page"
      >
        Skip to content
      </a>
      <NavRail me={me} />
      <main id="main" tabIndex={-1} className={fullChat ? "sr-only" : "flex min-w-0 flex-1 flex-col outline-none"}>
        <TopBar me={me} />
        {/* A sheet over the page, the page dimmed below it (P:1218-1255). */}
        <RD.Root open={frame.calendar !== 'closed'} onOpenChange={(o) => !o && frame.setCalendar('closed')}>
          <RD.Portal>
            <RD.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)]" />
            <RD.Content aria-describedby={undefined} className="fixed inset-x-0 top-0 z-50 outline-none">
              <RD.Title className="sr-only">Calendar</RD.Title>
            <div className="max-h-[88dvh] overflow-y-auto overscroll-contain border-b border-strong bg-page p-6 phone:max-h-[100dvh] phone:p-4">
              <div className="mx-auto max-w-[1040px]">
                <CalendarOverlay
                  key={frame.calendar}
                  anchor={today}
                  now={new Date()}
                  finding={frame.calendar === 'find'}
                  onClose={() => frame.setCalendar('closed')}
                  onWork={(day) => {
                    frame.workOn(day);
                    void navigate('/today');
                  }}
                />
              </div>
            </div>
            </RD.Content>
          </RD.Portal>
        </RD.Root>
        {/* A block box, so a page's own mx-auto centres it instead of
            shrinking it to its content inside the column. */}
        <div className={`w-full min-w-0 ${frame.layout.phone ? 'pb-[60px]' : ''}`}>
          <VendorBanner />
          {frame.permissionsOpen && me.isSuperadmin && <PagePermissions me={me} />}
          <CompanyRouteBoundary me={me}><RouteGuard me={me}>
            <Suspense fallback={<p role="status" className="p-6">Loading the working view…</p>}><Outlet /></Suspense>
          </RouteGuard></CompanyRouteBoundary>
        </div>
      </main>
      <ChatRail />
      {frame.layout.phone && <TabBar me={me} />}
      <CommandPalette me={me} />
    </div>
  );
}
